import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Dialog,
  AppBar,
  Toolbar,
  CircularProgress,
} from "@mui/material";

// Icons
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";

// Context & Services
import AppDataContext from "../../common/appContext";
import { Game5On5Services } from "../../services/game5On5.Service";
import { PlayerProfileService } from "../../services/playerProfile.Service";
import { baseUrl, Video_baseUrl } from "../../services/AzureStorage";

// Shared Components
import EmptyUI from "../../sharedComponents/emptyStateUIContainers/EmptyUI";
import TitleTooltip from "../../sharedComponents/Tooltip/TitleTooltip";
import GameCard from "./GameCard";
import Helper from "../../common/Helper";

export default function FiveOnFiveGameLeaders() {
  const { selectedEventId, user } = useContext(AppDataContext);

  const [playersList, setPlayersList] = useState([]);
  const [games, setGames] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selectedEventId || !user?.loginID) return;

    const init = async () => {
      setLoading(true);
      const players = await fetchPlayers();
      await fetchGames(players);
      setLoading(false);
    };

    init();
  }, [selectedEventId, user?.loginID]);

  const openNewTab = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handlePlay = (video) => setActive(video);

  const fetchPlayers = async () => {
    try {
      const res = await PlayerProfileService.getPlayerListByEventIdYear(
        selectedEventId
      );

      const players = (res?.data || []).map((p) => ({
        id: p.playerID,
        name: p.playerName,
        headShotUrl: p.headshotURL ? baseUrl(p.headshotURL) : "",
      }));

      setPlayersList(players);
      return players;
    } catch {
      setPlayersList([]);
      return [];
    }
  };

  const fetchGames = async (players = playersList) => {
    try {
      const res = await Game5On5Services.get5On5GameVideos(selectedEventId);

      const gamesData = res?.data?.data || [];

      const map = new Map(players.map((p) => [Number(p.id), p]));

      const parsePlayers = (ids = "") =>
        ids
          .split(",")
          .filter(Boolean)
          .map((id) => map.get(Number(id)))
          .filter(Boolean);

      const formatted = gamesData.map((g) => ({
        ...g,
        team1PlayersList: parsePlayers(g.team1PlayerIds),
        team2PlayersList: parsePlayers(g.team2PlayerIds),
      }));

      setGames(formatted);
    } catch {
      setGames([]);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #f8fafc, #eef2ff)",
        px: 3,
        py: 5,
      }}
    >
      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress size={45} thickness={4} />
        </Box>
      )}

      {!loading && games?.length === 0 && (
        <Grid container p={1}>
          <Grid item lg={12}>
            <EmptyUI
              heading="No games available yet."
              subHeading="Once games are uploaded, they'll appear here."
              icon={<SportsEsportsIcon />}
              styles={{
                minHeight: "30vh",
                background: "linear-gradient(135deg, #f9fafb, #eef2ff)",
                borderRadius: "16px",
                border: "1px solid rgba(0,0,0,0.05)",
              }}
              iconStyles={{
                fontSize: "clamp(10rem, 1vw + 8rem, 10rem)",
                color: "#8c9dc4",
              }}
            />
          </Grid>
        </Grid>
      )}

      {!loading && games.length > 0 && (
        <Grid container spacing={3}>
          {games.map((video) => (
            <Grid item xs={12} sm={6} md={3} key={video.gameVideosId}>
              <GameCard
                video={video}
                openNewTab={openNewTab}
                handlePlay={handlePlay}
                TitleTooltip={TitleTooltip}
                selectedEventId={selectedEventId}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog
        open={Boolean(active)}
        onClose={() => setActive(null)}
        fullWidth
        maxWidth="md"
      >
        {active && (
          <Box sx={{ background: "#000" }}>
            <AppBar position="static" sx={{ background: "#111827" }}>
              <Toolbar
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  {active.gameName}
                </Typography>

                <Box sx={{ display: "flex", gap: 1 }}>
                  {Helper.isHttpVideoURL(active.url) && (
                    <TitleTooltip title="Open in new tab">
                      <IconButton
                        onClick={() => openNewTab(active.url)}
                        sx={{ color: "#fff" }}
                      >
                        <OpenInNewIcon />
                      </IconButton>
                    </TitleTooltip>
                  )}

                  <IconButton
                    onClick={() => setActive(null)}
                    sx={{ color: "#fff" }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Toolbar>
            </AppBar>

            {!Helper.isHttpVideoURL(active.url) ? (
              <video
                src={Video_baseUrl(active.url)}
                controls
                autoPlay
                style={{
                  width: "100%",
                  height: "70vh",
                  backgroundColor: "#000",
                }}
              />
            ) : (
              <iframe
                src={active.url}
                title={active.gameName}
                style={{
                  width: "100%",
                  height: "70vh",
                  border: "none",
                }}
                loading="lazy"
                allowFullScreen
              />
            )}
          </Box>
        )}
      </Dialog>
    </Box>
  );
}
